import { EvaluationDuration } from 'domains/calendars/types';

import { useSignupDirectCtx } from 'domains/signupDirect/context';
import { DIRECT_SIGNUP_MIXPANEL_EVENTS } from 'domains/signupDirect/misc/mixpanelTracking';
import { useSignupEvaluationCalendar } from 'domains/signupDirect/misc/useSignupEvaluationCalendar';
import { EvaluationScheduleFormType } from 'domains/signupDirect/schemas/evaluationScheduleSchema';
import { useDisplayTimezone } from 'domains/timezone/useDisplayTimezone';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTrack } from 'utils/hooks/useMixPanel';

import { Button, FormLayout, Heading, LoadingDots, Paragraph, SubmitButton, Callout } from '@expressable/syntax-ds';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMinimumAvailability } from 'domains/signupDirect/misc/useMinimumAvailability';
import { SingleEvaluationSchema } from 'domains/signupDirect/schemas/evaluationScheduleSchema';
import { useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { routes } from 'router/routes';
import { useAutoScrollToTop } from 'utils/hooks/useAutoScrollToTop/useAutoScrollToTop';
import { useSplitEvaluationCtxV2 } from '../SplitEvaluationsScheduleSectionV2';
import { FirstSplitTimeSlotPickerV2 } from './Picker';
import { SingleEvaluationSchemaTypeV2 } from 'domains/signupDirect/schemas/evaluationScheduleSchemaV2';
import { useSignupNavigationGuard } from 'domains/signupDirect/misc/useSignupNavigationGuard';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { SignupConsultationDualBailout } from 'domains/signupDirect/misc/SignupConsultationDualBailout';
import { useOnBailoutDueToAvailability, useOnBailoutDueToQuestion } from 'domains/signupDirect/misc/jotform/hooks';

export const EvaluationScheduleFirstSplitV2 = () => {
  useAutoScrollToTop();
  useSignupNavigationGuard();
  const { onSubmitFirstSlot } = useSplitEvaluationCtxV2();
  const { filters } = useSignupDirectCtx();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { track } = useTrack();
  const onBailoutDueToAvailability = useOnBailoutDueToAvailability();
  const onBailoutDueToQuestion = useOnBailoutDueToQuestion();

  if (!filters) throw 'Expected filters to be set';

  const methods = useForm<SingleEvaluationSchemaTypeV2>({
    resolver: zodResolver(SingleEvaluationSchema),
    defaultValues: {
      type: 'single',
    },
  });

  const { handleSubmit } = methods;

  const { isLoading, evaluationCalendarQuery } = useSignupEvaluationCalendar(filters, EvaluationDuration.Split);
  const { isRedirecting } = useMinimumAvailability({
    evaluationCalendarQuery,
  });

  const onSwitchToSingle = useCallback(() => {
    track(DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.EVAL.SCHEDULE.BAILOUT_TO_SINGLE, {
      step: DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.EVAL.SCHEDULE.SPLIT_FIRST,
    });
    navigate(routes.signupDirect.evaluation.schedule.single);
  }, []);

  if (isLoading || isRedirecting) {
    return <LoadingDots className="flex flex-col h-max static mt-48" message={t('directSignup.schedule.loading')} />;
  }

  const { signupBookingCopiesUpdate } = useFlags();

  return (
    <>
      <BookingCopy onSwitchToSingle={onSwitchToSingle} signupBookingCopiesUpdate={signupBookingCopiesUpdate} t={t} />
      <FormProvider {...methods}>
        <FormLayout
          className="mobile-xs-d:gap-e40 tablet-d:gap-e56 pb-e128"
          onSubmit={handleSubmit(d => {
            onSubmitFirstSlot(d.timeSlot);
          })}
        >
          <FormLayout.Fields className="mobile-xs-d:gap-e24 tablet-d:gap-e32">
            <FormFields />
            <SignupConsultationDualBailout
              onBailoutToConsultDueToAvailability={onBailoutDueToAvailability}
              onBailoutToConsultDueToQuestion={onBailoutDueToQuestion}
            />
          </FormLayout.Fields>

          <FormLayout.CTA sticky className="flex mobile-d:flex-col w-full">
            <SubmitButton data-testid="picktime-submit-button">{t('directSignup.eval.schedule.submit')}</SubmitButton>
          </FormLayout.CTA>
        </FormLayout>
      </FormProvider>
    </>
  );
};

const FormFields = () => {
  const { filters, isResumedLead } = useSignupDirectCtx();
  useTrack(DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.EVAL.SCHEDULE.SPLIT_FIRST, { isResumedLead });
  if (!filters) {
    throw 'Expected filters to be set';
  }

  const { displayTimezone } = useDisplayTimezone();

  const { setValue } = useFormContext<EvaluationScheduleFormType>();

  const data = useSignupEvaluationCalendar(filters, EvaluationDuration.Split);

  useEffect(() => {
    if (displayTimezone) setValue('timeZone', displayTimezone.resolvedName);
  }, [displayTimezone]);

  if (data.isLoading) {
    throw new Error('Expected data to be loaded Single Eval fields');
  }

  return <FirstSplitTimeSlotPickerV2 calendarData={data} name="timeSlot" />;
};

interface BookingCopyProps {
  onSwitchToSingle: () => void;
  signupBookingCopiesUpdate: boolean;
  t: (key: string) => string;
}

/* Returns a component that displays different booking copies based on the signupBookingCopiesUpdate flag. */
const BookingCopy: React.FC<BookingCopyProps> = ({ onSwitchToSingle, signupBookingCopiesUpdate, t }) => {
  if (signupBookingCopiesUpdate) {
    return (
      <div>
        <Heading level="h1">{t('directSignup.schedule.split.first.bookingTitle')}</Heading>
        <Paragraph size="lg">{t('directSignup.schedule.split.first.bookingSubtitle.1')}</Paragraph>
        <Paragraph size="lg">
          {t('directSignup.schedule.split.first.bookingSubtitle.2')}{' '}
          <Button
            as="a"
            role="button"
            variant="link"
            tabIndex={0}
            onClick={onSwitchToSingle}
            size="lg"
            className="text-left inline"
          >
            {t('directSignup.schedule.split.first.switchToSingle')}
          </Button>
        </Paragraph>
        <Callout type="info">{t('directSignup.schedule.virtualTherapySellout')}</Callout>
      </div>
    );
  }

  return (
    <div>
      <Heading level="h1">{t('directSignup.schedule.split.first.title')}</Heading>
      <Paragraph size="lg">
        {t('directSignup.schedule.split.first.subTitle')}{' '}
        <Button
          as="a"
          role="button"
          variant="link"
          tabIndex={0}
          onClick={onSwitchToSingle}
          size="lg"
          className="text-left inline"
        >
          {t('directSignup.schedule.split.first.switchToSingle')}
        </Button>
      </Paragraph>
    </div>
  );
};
